"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { catalogRefreshEvent } from "@/app/_lib/catalog-cache-policy";
import type {
  CatalogFamilySummary,
  PublicCatalogProduct,
} from "@/domain/catalog/catalog-product";
import type { CatalogSearchResult } from "@/domain/catalog/search";
import { ProductSection } from "@/components/home/product-section";

const homeCategoryPageSize = 12;
const requestTimeoutMs = 15_000;

export interface HomeCategoryProducts {
  readonly familySlug: string;
  readonly products: readonly PublicCatalogProduct[];
  readonly total: number;
}

interface HomeCategoryState extends HomeCategoryProducts {
  readonly errorMessage: string | null;
  readonly hasMore: boolean;
  readonly isLoading: boolean;
  readonly offset: number;
}

export interface HomeProductSectionsProps {
  readonly families: readonly CatalogFamilySummary[];
  readonly initialCategories: readonly HomeCategoryProducts[];
}

export function HomeProductSections({
  families,
  initialCategories,
}: Readonly<HomeProductSectionsProps>) {
  const initialState = initialCategories.map(toCategoryState);
  const [categories, setCategories] = useState<HomeCategoryState[]>(initialState);
  const [nextCategoryIndex, setNextCategoryIndex] = useState(initialState.length);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const categoryFallbackRef = useRef<HTMLButtonElement>(null);
  const categoriesRef = useRef(categories);
  const nextCategoryIndexRef = useRef(nextCategoryIndex);
  const categoryLoadingRef = useRef(false);
  const pageLoadingRef = useRef(new Set<string>());
  const controllersRef = useRef(new Map<string, AbortController>());
  const isUnmountedRef = useRef(false);
  const refreshControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  useEffect(() => {
    nextCategoryIndexRef.current = nextCategoryIndex;
  }, [nextCategoryIndex]);

  useEffect(() => {
    const controllers = controllersRef.current;
    return () => {
      isUnmountedRef.current = true;
      for (const controller of controllers.values()) {
        controller.abort();
      }
      controllers.clear();
    };
  }, []);

  const loadCategoryPage = useCallback(async (
    familySlug: string,
    offset: number,
    signal: AbortSignal,
  ): Promise<CatalogSearchResult<PublicCatalogProduct>> => {
    const searchParams = new URLSearchParams({
      familySlug,
      limit: String(homeCategoryPageSize),
      offset: String(offset),
      sort: "relevance",
    });
    const response = await fetch(`/api/catalog/search?${searchParams.toString()}`, { signal });

    if (!response.ok) {
      throw new Error("HOME_CATEGORY_REQUEST_FAILED");
    }

    return (await response.json()) as CatalogSearchResult<PublicCatalogProduct>;
  }, []);

  useEffect(() => {
    // Periodic and visibility refreshes revalidate the first page of every visible
    // category only. Deeper loaded pages keep their content instead of fanning out one
    // request per loaded offset; they move forward on the next explicit user action.
    const refreshLoadedCategories = () => {
      refreshControllerRef.current?.abort();
      const controller = new AbortController();
      refreshControllerRef.current = controller;
      const loaded = categoriesRef.current.filter((category) => category.products.length > 0);
      void Promise.all(loaded.map(async (category) => ({
        familySlug: category.familySlug,
        firstPage: await loadCategoryPage(category.familySlug, 0, controller.signal),
      }))).then((updates) => {
        if (controller.signal.aborted) {
          return;
        }
        const bySlug = new Map(updates.map((update) => [update.familySlug, update.firstPage]));
        setCategories((previous) => previous.map((category) => {
          const firstPage = bySlug.get(category.familySlug);
          if (!firstPage) {
            return category;
          }
          const refreshedFirstPage = firstPage.products ?? [];
          const refreshedIds = new Set(refreshedFirstPage.map((product) => product.id));
          const keptDeeperPages = category.products
            .slice(homeCategoryPageSize)
            .filter((product) => !refreshedIds.has(product.id));
          const products = [...refreshedFirstPage, ...keptDeeperPages];
          const total = typeof firstPage.total === "number" ? firstPage.total : category.total;
          return {
            ...category,
            products,
            total,
            offset: products.length,
            hasMore: refreshedFirstPage.length > 0 && products.length < total,
          };
        }));
      }).catch(() => {
        // Keep the previous cards on a transient failure; the next tick retries.
      }).finally(() => {
        if (refreshControllerRef.current === controller) {
          refreshControllerRef.current = null;
        }
      });
    };
    window.addEventListener(catalogRefreshEvent, refreshLoadedCategories);
    return () => {
      window.removeEventListener(catalogRefreshEvent, refreshLoadedCategories);
      refreshControllerRef.current?.abort();
    };
  }, [loadCategoryPage]);

  const loadMoreProducts = useCallback(async (familySlug: string) => {
    const current = categoriesRef.current.find((category) => category.familySlug === familySlug);

    if (!current || !current.hasMore || pageLoadingRef.current.has(familySlug)) {
      return;
    }

    pageLoadingRef.current.add(familySlug);
    setCategories((previous) => previous.map((category) => (
      category.familySlug === familySlug
        ? { ...category, errorMessage: null, isLoading: true }
        : category
    )));

    const controller = new AbortController();
    controllersRef.current.set(familySlug, controller);
    const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);

    try {
      const result = await loadCategoryPage(familySlug, current.offset, controller.signal);
      const seenIds = new Set(current.products.map((product) => product.id));
      const uniqueProducts = (result.products ?? []).filter((product) => {
        if (seenIds.has(product.id)) {
          return false;
        }
        seenIds.add(product.id);
        return true;
      });
      const nextProducts = [...current.products, ...uniqueProducts];
      const nextTotal = typeof result.total === "number" ? result.total : current.total;
      const nextOffset = current.offset + (result.products?.length ?? 0);

      if (!isUnmountedRef.current) {
        setCategories((previous) => previous.map((category) => (
          category.familySlug === familySlug
            ? {
                ...category,
                errorMessage: null,
                hasMore: uniqueProducts.length > 0 && nextProducts.length < nextTotal,
                isLoading: false,
                offset: nextOffset,
                products: nextProducts,
                total: nextTotal,
              }
            : category
        )));
      }
    } catch {
      if (!isUnmountedRef.current) {
        setCategories((previous) => previous.map((category) => (
          category.familySlug === familySlug
            ? {
                ...category,
                errorMessage: "No se pudieron cargar más productos.",
                isLoading: false,
              }
            : category
        )));
      }
    } finally {
      clearTimeout(timeoutId);
      controllersRef.current.delete(familySlug);
      pageLoadingRef.current.delete(familySlug);
    }
  }, [loadCategoryPage]);

  const loadNextCategory = useCallback(async () => {
    if (categoryLoadingRef.current) {
      return;
    }

    const family = families[nextCategoryIndexRef.current];
    if (!family) {
      return;
    }

    categoryLoadingRef.current = true;
    setIsLoadingCategory(true);
    setCategoryError(null);

    const controller = new AbortController();
    const key = `category:${family.slug}`;
    controllersRef.current.set(key, controller);
    const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);

    try {
      const result = await loadCategoryPage(family.slug, 0, controller.signal);
      if (!isUnmountedRef.current) {
        setCategories((previous) => [...previous, toCategoryState({
          familySlug: family.slug,
          products: result.products ?? [],
          total: typeof result.total === "number" ? result.total : result.products?.length ?? 0,
        })]);
        setNextCategoryIndex((previous) => previous + 1);
      }
    } catch {
      if (!isUnmountedRef.current) {
        setCategoryError("No se pudo cargar la siguiente categoría.");
      }
    } finally {
      clearTimeout(timeoutId);
      controllersRef.current.delete(key);
      categoryLoadingRef.current = false;
      setIsLoadingCategory(false);
    }
  }, [families, loadCategoryPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === "undefined" || nextCategoryIndex >= families.length || isLoadingCategory) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          void loadNextCategory();
        }
      },
      { rootMargin: "720px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [families.length, isLoadingCategory, loadNextCategory, nextCategoryIndex]);

  useEffect(() => {
    const fallback = categoryFallbackRef.current;
    if (fallback) {
      fallback.hidden = typeof IntersectionObserver !== "undefined";
    }
  });

  const categoriesBySlug = new Map(
    categories.map((category) => [category.familySlug, category]),
  );
  const sections = families
    .slice(0, nextCategoryIndex)
    .map((family) => ({
      family,
      state: categoriesBySlug.get(family.slug),
    }))
    .filter((section): section is { family: CatalogFamilySummary; state: HomeCategoryState } => {
      const state = section.state;
      return state !== undefined && state.products.length > 0;
    });

  return (
    <>
      {sections.map(({ family, state }) => (
        <ProductSection
          errorMessage={state.errorMessage}
          familySlug={family.slug}
          hasMore={state.hasMore}
          isLoading={state.isLoading}
          key={family.slug}
          onLoadMore={() => void loadMoreProducts(family.slug)}
          onRetry={() => void loadMoreProducts(family.slug)}
          products={sortCategoryProducts(state.products)}
          title={family.name}
        />
      ))}
      {nextCategoryIndex < families.length || isLoadingCategory || categoryError ? (
        <div
          aria-live="polite"
          className="mt-6 min-h-10 text-center text-sm font-bold text-muted"
          ref={sentinelRef}
        >
          {categoryError ? (
            <div className="flex flex-col items-center gap-2">
              <p>{categoryError}</p>
              <button
                className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-white"
                onClick={() => void loadNextCategory()}
                type="button"
              >
                Reintentar
              </button>
            </div>
          ) : isLoadingCategory ? (
            "Cargando categoría..."
          ) : nextCategoryIndex < families.length ? (
            <button
              className="rounded-md border border-primary px-4 py-2 text-sm font-bold text-primary"
              hidden
              onClick={() => void loadNextCategory()}
              ref={categoryFallbackRef}
              type="button"
            >
              Cargar más
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function toCategoryState(category: HomeCategoryProducts): HomeCategoryState {
  const total = category.total;

  return {
    ...category,
    errorMessage: null,
    hasMore: category.products.length < total,
    isLoading: false,
    offset: category.products.length,
    total,
  };
}

function sortCategoryProducts(
  products: readonly PublicCatalogProduct[],
): readonly PublicCatalogProduct[] {
  return [...products].sort((leftProduct, rightProduct) => {
    if (rightProduct.utilityRankBob !== leftProduct.utilityRankBob) {
      return rightProduct.utilityRankBob - leftProduct.utilityRankBob;
    }

    return leftProduct.name.localeCompare(rightProduct.name);
  });
}
